import { Alert } from '@/shared/components/rebranded/Alert/Alert';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { FieldSet, Legend } from '../../shared/components/Form/Form';
import { FieldProps, NumberSchema, ObjectSchema } from '../types';
import { joinErrors, orderProperties } from '../validation/utils';
import { CounterField } from './CounterField';

type ObjectCounterFieldProps = FieldProps<
  ObjectSchema<{ [key: string]: NumberSchema }>
> & {
  totalCountText?: string;
};

// JSON Schemas should include a _total property
// when using the ObjectCounterField ui widget to
// compute a running total.
const COUNTER_PROP = '_total';

const countValues = (value: { [key: string]: number | null }) => {
  return Object.values(value || {}).reduce(
    (acc: number, val) => acc + (val || 0),
    0
  );
};

export const ObjectCounterField = (props: ObjectCounterFieldProps) => {
  const { getText } = useTranslations();

  const handleChange = (subField: string, value: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [subField]: _, [COUNTER_PROP]: __, ...others } = props.value;
    const total = countValues(others) + value;

    props.onChange({
      target: {
        name: props.name,
        value: {
          ...props.value,
          [subField]: value,
          [COUNTER_PROP]: total
        }
      }
    });
  };

  const { schema } = props;

  const $uiOptions = schema.$uiOptions || {};
  const errors = props.errors || {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [COUNTER_PROP]: total, ...actualCounters } = props.value;

  const properties = schema.properties || {};

  return (
    <FieldSet>
      {schema.title && <Legend>{schema.title}</Legend>}
      {schema.$uiOptions?.hint && (
        <div className="dsm-form-hint">{schema.$uiOptions.hint}</div>
      )}

      <div className="data-hj-suppress" style={{ marginTop: 16 }}>
        {orderProperties(
          Object.keys(properties).filter(property => property !== COUNTER_PROP),
          (schema.$uiOptions || {}).propertyOrder
        ).map(field => {
          const name = `${props.name}_${field}`;
          return (
            <CounterField
              schema={properties[field] as NumberSchema}
              key={name}
              name={name}
              formName={props.formName}
              id={`${props.id}_${field}`}
              aria-describedby={`${props.id}_description`}
              value={(props.value || {})[field]}
              errors={errors[field]}
              touched={(props.touched || {})[field]}
              onChange={e => handleChange(field, e.target.value)}
              onBlur={() => {}}
              forceShowError={props.forceShowError}
              single={false}
              readOnly={props.readOnly}
            />
          );
        })}

        {$uiOptions.totalCountText && (
          <div style={{ margin: 0, marginTop: 16, color: '#888' }}>
            {$uiOptions.totalCountText.replace(
              /{count}/g,
              countValues(actualCounters).toString()
            )}
          </div>
        )}

        {props.forceShowError &&
          errors[COUNTER_PROP] &&
          errors[COUNTER_PROP].length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Alert
                variant="error"
                title={joinErrors(errors[COUNTER_PROP], getText)}
                fluid
              />
            </div>
          )}
      </div>
    </FieldSet>
  );
};
